"use client";

import { useState, useEffect, useCallback } from "react";
import useSWR from "swr";
import { BetAdviseItem } from "./betAdviseItem";
import { useTranslations } from "next-intl";
import { addHours, formatDuration, intervalToDuration } from "date-fns";

import { debounce } from "@/utils/debounce";

import "@/components/organisms/Table/table.scss";

import { useRouter, useSearchParams } from "next/navigation";

const fetcher = (url: string): Promise<any> => fetch(url).then((res) => res.json());

interface BetAdvisesListProps {
  initialData: any;
  initialPage: number;
  status: string;
  totalPages: number;
}

const BetAdvisesList: React.FC<BetAdvisesListProps> = ({ initialData, initialPage, status, totalPages }) => {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [page, setPage] = useState<number>(initialPage);
  const [requestedPage, setRequestedPage] = useState<number>(initialPage);
  const [loading, setLoading] = useState<boolean>(false);
  const [timer, setTimer] = useState<string>("");

  const t = useTranslations("table");
  const tBets = useTranslations("bets");

  const calculateRemainingTime = (cacheUpdatedAt: string) => {
    const expiryTime = addHours(new Date(cacheUpdatedAt), 4);
    const duration = intervalToDuration({ start: new Date(), end: expiryTime });
    return formatDuration(duration, { format: ["hours", "minutes"], delimiter: " " })
      .replace("hours", "h")
      .replace("minutes", "m");
  };

  useEffect(() => {
    setTimer(calculateRemainingTime(initialData.cacheUpdatedAt));

    const interval = setInterval(() => {
      setTimer(calculateRemainingTime(initialData.cacheUpdatedAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [initialData.cacheUpdatedAt]);

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set("page", page.toString());
    router.replace(`?${currentParams.toString()}`, { scroll: false });
  }, [page]);

  const response = useSWR(`/api/bets/advises?status=${status}&page=${page}`, fetcher, {
    fallbackData: initialData,
    revalidateOnFocus: false,
    onSuccess: (data) => {
      setLoading(false);
      setTimer(calculateRemainingTime(data.cacheUpdatedAt));
    }
  });

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setLoading(true);
      setRequestedPage(newPage);
      debouncedOnPageChange(newPage);
    } else {
      setRequestedPage(page);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      handlePageChange(requestedPage - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      handlePageChange(requestedPage + 1);
    }
  };

  const handlePageInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPage = Number(event.target.value);
    setRequestedPage(newPage);
  };

  const handlePageBlur = () => {
    if (!isNaN(requestedPage) && requestedPage >= 1 && requestedPage <= totalPages) {
      handlePageChange(requestedPage);
    } else {
      setRequestedPage(page);
    }
  };

  const debouncedOnPageChange = useCallback(
    debounce((newPage: number) => {
      setPage(newPage);
    }, 500),
    [setPage]
  );

  return (
    <div>
      <div className={`bet-advises-list ${loading ? "loading" : "loaded"}`}>
        {response.data.betAdvises.map((advise: any) => (
          <BetAdviseItem key={advise.id} advise={advise} />
        ))}
      </div>
      <div className="text-end mb-2 me-2">
        {timer && <span>{tBets('nextUpdate')}: {timer}</span>}
      </div>
      {totalPages > 1 && (
        <div className="pagination-controls pt-0 mt-0">
          <button 
            className="btn btn-primary-outline btn-sm" 
            onClick={handlePrevPage} 
            disabled={page <= 1}
          >
            {t("previous")}
          </button>
          <input
            className="form-control form-control-sm"
            type="number"
            value={requestedPage}
            onChange={handlePageInput}
            onBlur={handlePageBlur}
            max={totalPages}
          />
          <span className='w-100'>{t("ofPages")} {totalPages}</span>
          <button 
            className="btn btn-primary-outline btn-sm" 
            onClick={handleNextPage} 
            disabled={page >= totalPages}
          >
            {t("next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default BetAdvisesList;